/**
 * Направления работы проекта.
 *
 * Направление — это группировка элементов, а не отдельная сущность со своим
 * прогрессом: процент готовности здесь не считается, показываются счётчики
 * открытых элементов (документ «Структура интерфейса», §7.9).
 *
 * Порядок задаётся стрелками, а не перетаскиванием: интерфейс не должен
 * зависеть только от drag-and-drop (документ «Структура интерфейса», §22).
 */

import { ArrowDownIcon, ArrowUpIcon, Loader2Icon, PlusIcon, TrashIcon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import {
  WORKSTREAM_STATUSES,
  WORKSTREAM_STATUS_LABELS_RU,
  type UserDto,
  type WorkstreamDto,
  type WorkstreamStatus,
} from '@project-os/contracts';
import { ApiError } from '@/api/client';
import {
  useArchiveWorkstream,
  useCreateWorkstream,
  useReorderWorkstreams,
  useUpdateWorkstream,
  useWorkstreams,
} from '@/api/workstreams';
import { StatusBadge } from '@/components/common/status-badge';
import { EmptyState, ErrorState } from '@/components/common/states';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { pluralRu } from '@/lib/format';

const NONE = '__none__';

const STATUS_TONES: Record<WorkstreamStatus, 'neutral' | 'success' | 'danger'> = {
  NOT_STARTED: 'neutral',
  ACTIVE: 'success',
  BLOCKED: 'danger',
  DONE: 'neutral',
};

export function WorkstreamsCard({
  projectId,
  members,
  canEdit,
}: {
  projectId: string;
  members: UserDto[];
  canEdit: boolean;
}) {
  const { data: workstreams, isPending, isError, error, refetch } = useWorkstreams(projectId);
  const [newName, setNewName] = useState('');

  const create = useCreateWorkstream(projectId);
  const update = useUpdateWorkstream(projectId);
  const archive = useArchiveWorkstream(projectId);
  const reorder = useReorderWorkstreams(projectId);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await create.mutateAsync({ name, status: 'NOT_STARTED' });
      setNewName('');
      toast.success('Направление добавлено');
    } catch (mutationError) {
      toast.error(
        mutationError instanceof ApiError ? mutationError.message : 'Не удалось добавить',
      );
    }
  };

  const move = async (index: number, delta: number) => {
    if (!workstreams) return;
    const target = index + delta;
    if (target < 0 || target >= workstreams.length) return;

    const order = workstreams.map((workstream) => workstream.id);
    const [moved] = order.splice(index, 1);
    if (moved) order.splice(target, 0, moved);

    try {
      await reorder.mutateAsync({ order });
    } catch (mutationError) {
      toast.error(
        mutationError instanceof ApiError ? mutationError.message : 'Не удалось изменить порядок',
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Направления работы</CardTitle>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
          Группируют элементы проекта по областям: исследование, продукт, юридические вопросы. У
          каждого направления может быть ответственный.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {isPending ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : workstreams.length === 0 ? (
          <EmptyState
            title="Направлений пока нет"
            description="Без направлений всё работает, но с ними легче понять, в какой области проекта скопились открытые вопросы."
          />
        ) : (
          <ul className="divide-y rounded-md border">
            {workstreams.map((workstream, index) => (
              <WorkstreamRow
                key={workstream.id}
                projectId={projectId}
                workstream={workstream}
                members={members}
                canEdit={canEdit}
                isFirst={index === 0}
                isLast={index === workstreams.length - 1}
                busy={update.isPending || archive.isPending || reorder.isPending}
                onMove={(delta) => void move(index, delta)}
                onChangeStatus={(status) =>
                  void update.mutateAsync({ workstreamId: workstream.id, status })
                }
                onChangeOwner={(ownerId) =>
                  void update.mutateAsync({ workstreamId: workstream.id, ownerId })
                }
                onArchive={() => void archive.mutateAsync(workstream.id)}
              />
            ))}
          </ul>
        )}

        {canEdit ? (
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleCreate();
                }
              }}
              placeholder="Название нового направления"
              maxLength={160}
            />
            <Button
              variant="outline"
              disabled={!newName.trim() || create.isPending}
              onClick={() => void handleCreate()}
            >
              {create.isPending ? <Loader2Icon className="animate-spin" /> : <PlusIcon />}
              Добавить
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function WorkstreamRow({
  projectId,
  workstream,
  members,
  canEdit,
  isFirst,
  isLast,
  busy,
  onMove,
  onChangeStatus,
  onChangeOwner,
  onArchive,
}: {
  projectId: string;
  workstream: WorkstreamDto;
  members: UserDto[];
  canEdit: boolean;
  isFirst: boolean;
  isLast: boolean;
  busy: boolean;
  onMove: (delta: number) => void;
  onChangeStatus: (status: WorkstreamStatus) => void;
  onChangeOwner: (ownerId: string | null) => void;
  onArchive: () => void;
}) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5">
      <div className="min-w-40 flex-1">
        <Link
          to={`/projects/${projectId}/items?workstreamId=${workstream.id}`}
          className="text-sm font-medium hover:underline"
        >
          {workstream.name}
        </Link>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {workstream.counters.openItems === 0
            ? 'Открытых элементов нет'
            : pluralRu(workstream.counters.openItems, [
                'открытый элемент',
                'открытых элемента',
                'открытых элементов',
              ])}
          {workstream.counters.openBlockers > 0
            ? ` · ${pluralRu(workstream.counters.openBlockers, ['блокер', 'блокера', 'блокеров'])}`
            : ''}
        </p>
      </div>

      {canEdit ? (
        <>
          <Select
            value={workstream.status}
            disabled={busy}
            onValueChange={(value) => onChangeStatus(value as WorkstreamStatus)}
          >
            <SelectTrigger size="sm" className="w-40" aria-label="Состояние направления">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WORKSTREAM_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {WORKSTREAM_STATUS_LABELS_RU[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={workstream.owner?.id ?? NONE}
            disabled={busy}
            onValueChange={(value) => onChangeOwner(value === NONE ? null : value)}
          >
            <SelectTrigger size="sm" className="w-44" aria-label="Ответственный за направление">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Без ответственного</SelectItem>
              {members.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  {member.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={isFirst || busy}
              onClick={() => onMove(-1)}
              aria-label="Переместить выше"
            >
              <ArrowUpIcon className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={isLast || busy}
              onClick={() => onMove(1)}
              aria-label="Переместить ниже"
            >
              <ArrowDownIcon className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-muted-foreground hover:text-destructive"
              disabled={busy}
              onClick={onArchive}
              aria-label="Архивировать направление"
              title="Архивировать. Элементы направления сохранятся."
            >
              <TrashIcon className="size-3.5" />
            </Button>
          </div>
        </>
      ) : (
        <>
          <StatusBadge
            label={WORKSTREAM_STATUS_LABELS_RU[workstream.status]}
            tone={STATUS_TONES[workstream.status]}
          />
          <span className="w-44 truncate text-xs text-muted-foreground">
            {workstream.owner?.name ?? 'Без ответственного'}
          </span>
        </>
      )}
    </li>
  );
}
